import { Injectable } from '@angular/core';

/**
 * PathRoutingService — Registry of recognized URL path routes.
 *
 * Follows the QuickLinkService static-getter pattern for URL detection.
 * Each recognized path maps to a ComponentsToLoad value (string).
 *
 * SEMANTIC INVARIANT: Only recognized paths trigger path routing.
 * Unrecognized paths MUST fall through to standard behavior.
 *
 * AC1: First recognized path — /assetTag/?AssetTagId=X
 * AC2: Unrecognized paths fall through to standard behavior (handled in caller).
 */
@Injectable({
    providedIn: 'root'
})
export class PathRoutingService {
    /**
     * Registry of recognized path segments to component names.
     * Add new path routes here to extend the registry.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private static readonly recognizedPaths: Record<string, string> = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '/assetTag/': 'AssetTag'
    };

    /** Full current URL including query string */
    static get url(): string {
        return window?.location?.href || '';
    }

    /** Current pathname (no query string) */
    static get pathname(): string {
        return window?.location?.pathname || '';
    }

    /**
     * Returns true if the current URL matches the AssetTag path.
     * Supports both pathname-based (/assetTag/) and query-param-based (?AssetTagId=X) detection.
     */
    static get isAssetTag(): boolean {
        return this.pathname.includes('/assetTag/') || this.url.includes('AssetTagId=');
    }

    /**
     * Returns true if the current URL matches ANY recognized path in the registry.
     * Use this as the primary gate in customer-portal.component.ts.
     */
    static get isPathRoute(): boolean {
        return Object.keys(this.recognizedPaths).some(path => this.pathname.includes(path));
    }

    /**
     * Returns the component name string for the first matching recognized path,
     * or null if no path matches.
     */
    static get matchedComponent(): string | null {
        for (const [path, component] of Object.entries(this.recognizedPaths)) {
            if (this.pathname.includes(path)) {
                return component;
            }
        }
        return null;
    }

    /**
     * Reads a query parameter from the current URL's search string.
     * @param key - The query parameter key (case-sensitive)
     * @returns The decoded parameter value, or null if not present
     */
    static getQueryParam(key: string): string | null {
        const params = new URLSearchParams(window?.location?.search || '');
        return params.get(key);
    }
}
